import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { LEGAL_DOC_URL } from '@oy/types';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLegalDocumentById, signLegalDocument } from 'src/clients';
import { useScrolledToBottom, useTranslate } from 'src/hooks';
import { useLegalDocumentData } from 'src/hooks/data/useLegalDocumentData';
import { RootState } from 'src/redux/reducers/rootReducer';
import { ACTION_TYPES } from 'src/redux/types/signLegalDocumentType';
import { reportFunctionError } from 'src/utilities/sentry';
import useSWRMutation from 'swr/mutation';

type FetcherType = {
  arg: {
    documents: string[];
    agreementNumber?: string;
  };
};

export const useSignLegalDocumentModalImpl = () => {
  const { data: legalDocs, isShow, isSigned, mutate } = useLegalDocumentData();

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isCurrentDocsSigned, setIsCurrentDocsSigned] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [agreementNumber, setAgreementNumber] = useState<string | undefined>();
  const { lang } = useTranslate();
  const dispatch = useDispatch();
  const { isBottom, setRef, resetPosition } = useScrolledToBottom();
  const { showModal: show } = useSelector(
    (state: RootState) => state.signLegalDocument,
  );
  const [showModalEmailDidntOpen, setShowModalEmailDidntOpen] = useState(false);

  const openModal = useCallback(() => {
    dispatch({ type: ACTION_TYPES.SHOW_MODAL });
  }, [dispatch]);

  const closeModal = () => {
    dispatch({ type: ACTION_TYPES.CLOSE_MODAL });
  };

  const [payloadToSignDocuments, setPayloadToSignDocuments] = useState<
    string[]
  >([]);

  const [selectedDocument, setSelectedDocument] = useState<
    string | undefined
  >();

  const [isDownloading, setIsDownloading] = useState(false);

  const onBackToAllDocument = () => {
    setSelectedDocument(undefined);
  };

  const onClickReadDocument = (documentId: string) => {
    setSelectedDocument(documentId);
  };

  const onDownload = async () => {
    try {
      setIsDownloading(true);
      const ids = legalDocs.map(document => document.id);
      const filePromises = ids.map(async id => {
        const response = await getLegalDocumentById(id); // Assume this returns base64 data
        if (response.data.data) {
          const { file, file_name } = response.data.data; // Ensure response contains these fields

          // Convert base64 to blob
          const byteCharacters = atob(file);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });

          // Create download link
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = file_name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup
          URL.revokeObjectURL(link.href);
        }
      });

      await Promise.all(filePromises);
    } catch (exception) {
      reportFunctionError({
        name: 'onDownload PKS',
        message: 'Failed to download PKS b2xportal',
        domain: 'dashboard',
        exception,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const putSignLegalDocument = async (_url: string, options: FetcherType) => {
    closeModal();
    const res = await signLegalDocument(
      options.arg.documents,
      options.arg.agreementNumber,
    );
    return res.data?.success;
  };

  const { trigger, isMutating } = useSWRMutation(
    `${LEGAL_DOC_URL}/sign`,
    putSignLegalDocument,
    {
      onSuccess: () => {
        mutate();
        setShowSuccessModal(true);
      },
      onError: e => {
        openModal();
        reportFunctionError({
          name: 'Sign Legal Document',
          message: 'Failed to sign legal document',
          domain: 'dashboard',
          exception: e,
        });
      },
    },
  );

  const onCloseModal = () => {
    AnalyticsManager.instance().track(AnalyticsEvent.click_close_pkspopup, {
      lang: lang,
      platform: 'desktop',
      doctype: legalDocs.some(item => item.type === 'CUSTOM_AGREEMENT')
        ? 'suratketerangan'
        : 'pks',
    });

    setShowCloseModal(true);
  };

  const onCloseModalConfirmation = () => {
    setShowCloseModal(false);
    closeModal();
  };

  const onCancelCloseModalConfirmation = () => {
    setShowCloseModal(false);
    openModal();
  };

  const onSubmit = () => {
    AnalyticsManager.instance().track(AnalyticsEvent.click_submit_pks, {
      lang: lang,
      platform: 'desktop',
      doctype: legalDocs.some(item => item.type === 'CUSTOM_AGREEMENT')
        ? 'suratketerangan'
        : 'pks',
    });
    const documents = legalDocs.map(document => document.id);
    trigger({ documents, agreementNumber });
  };

  const onChecked = () => {
    setIsCurrentDocsSigned(prevState => !prevState);
  };

  const onLoadSuccess = (newNumPages: number) => {
    setNumPages(newNumPages);
  };

  const onCloseModalSuccess = () => {
    setShowSuccessModal(false);
  };

  const onAddSignedDocument = (documentId: string) => {
    setPayloadToSignDocuments(prev => [...prev, documentId]);

    const nextDocument = legalDocs.find(
      file =>
        file.id !== documentId &&
        file.status === 'NEED_TO_SIGN' &&
        !payloadToSignDocuments.includes(file.id),
    );

    if (nextDocument) {
      setSelectedDocument(nextDocument.id);
    } else {
      setSelectedDocument(undefined);
    }
  };

  const onChangeAgreementNumber = (value?: string) => {
    setAgreementNumber(value);
  };
  const onCloseModalEmailDidntOpen = () => {
    setShowModalEmailDidntOpen(false);
  };

  const state = {
    selectedDocument,
    show: show && isShow,
    isLoading: isDownloading,
    isCurrentDocsSigned,
    numPages,
    legalDocs,
    showCloseModal,
    showSuccessModal,
    isSubmitting: isMutating,
    isAlreadySigned: show && isSigned && !showModalEmailDidntOpen,
    payloadToSignDocuments,
    isBottom,
    agreementNumber,
    showModalEmailDidntOpen,
  };

  return {
    state,
    onCloseModal,
    onSubmit,
    onLoadSuccess,
    onChecked,
    onCloseModalConfirmation,
    onCancelCloseModalConfirmation,
    onCloseModalSuccess,
    onCloseAlreadySigned: closeModal,
    onDownload,
    onAddSignedDocument,
    onBackToAllDocument,
    onClickReadDocument,
    setRef,
    onChangeAgreementNumber,
    resetPosition,
    onCloseModalEmailDidntOpen,
    setShowModalEmailDidntOpen,
  };
};
